import React, { useState, useEffect, useContext, useMemo } from "react";
import StatisticsService from "../services/StatisticsService";
import CustomerService from "../services/CustomerService";
import BrandService from "../services/BrandService";
import ArticleService from "../services/ArticleService";
import { ApplicationContext } from "./ApplicationContext";
import { formatCurrency } from "./utils";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { toast } from "react-toastify";

const MONTH_NAMES = [
  "Januar", "Februar", "Mart", "April", "Maj", "Jun",
  "Jul", "Avgust", "Septembar", "Oktobar", "Novembar", "Decembar",
];

const AnalyticsPage = () => {
  const { userRole } = useContext(ApplicationContext);
  const isUser = userRole === "ROLE_USER";

  // Filter state
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [brands, setBrands] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState("");
  const [articles, setArticles] = useState([]);
  const [selectedArticleId, setSelectedArticleId] = useState("");

  // Data state
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load filter options on mount
  useEffect(() => {
    StatisticsService.getYears()
      .then((res) => setYears(res.data || []))
      .catch(() => {});

    if (!isUser) {
      CustomerService.getAll()
        .then((res) => setCustomers(res.data || []))
        .catch(() => {});
      BrandService.getAll()
        .then((res) => setBrands(res.data || []))
        .catch(() => {});
      ArticleService.getAllActiveArticles()
        .then((res) => setArticles(res.data || []))
        .catch(() => {});
    }
  }, [isUser]);

  const handleFetch = () => {
    if (!selectedYear) {
      toast.warning("Morate izabrati godinu.");
      return;
    }

    setLoading(true);
    const params = { year: selectedYear };
    if (selectedCustomerId) params.customerId = selectedCustomerId;
    if (selectedBrandId) params.brandId = selectedBrandId;
    if (selectedArticleId) params.articleId = selectedArticleId;

    StatisticsService.getTrends(params)
      .then((res) => {
        setTrendData(res.data);
        setLoading(false);
      })
      .catch((e) => {
        console.error(e);
        toast.error("Greska pri ucitavanju statistike.");
        setLoading(false);
      });
  };

  // Derive chart data from API response
  const chartData = useMemo(() => {
    if (!trendData || !trendData.monthly) return [];
    return trendData.monthly.map((m) => ({
      name: m.month,
      Kvantitet: Number(m.quantity || 0),
      Prihod: Number(m.revenue || 0),
    }));
  }, [trendData]);

  // Summary calculations
  const summary = useMemo(() => {
    if (!trendData || !trendData.monthly) {
      return { totalQuantity: 0, totalRevenue: 0, avgOrder: 0, activeMonths: 0 };
    }
    const md = trendData.monthly;
    const totalQuantity = md.reduce((sum, m) => sum + Number(m.quantity || 0), 0);
    const totalRevenue = md.reduce((sum, m) => sum + Number(m.revenue || 0), 0);
    const activeMonths = md.filter((m) => Number(m.quantity || 0) > 0 || Number(m.revenue || 0) > 0).length;
    const avgOrder = activeMonths > 0 ? totalRevenue / activeMonths : 0;
    return { totalQuantity, totalRevenue, avgOrder, activeMonths };
  }, [trendData]);

  const quantityLabel = isUser ? "Vas ukupan kvantitet" : "Ukupan kvantitet";
  const revenueLabel = isUser ? "Vas ukupan iznos" : "Ukupan prihod";
  const pageTitle = isUser ? "Vasa statistika" : "Statistika i analitika";

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{pageTitle}</h1>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
          {/* Year */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Godina <span className="text-red-500">*</span>
            </label>
            <select
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              <option value="">Izaberite godinu</option>
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          {/* Customer (hidden for ROLE_USER) */}
          {!isUser && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Klijent</label>
              <select
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
              >
                <option value="">Svi klijenti</option>
                {customers.map((c) => (
                  <option key={c.customer_id} value={c.customer_id}>
                    {c.nameOfTheLegalEntity || c.influencerName || `Klijent ${c.customer_id}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Brand (hidden for ROLE_USER) */}
          {!isUser && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Brend</label>
              <select
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                value={selectedBrandId}
                onChange={(e) => setSelectedBrandId(e.target.value)}
              >
                <option value="">Svi brendovi</option>
                {brands.map((b) => (
                  <option key={b.brand_id} value={b.brand_id}>
                    {b.brandName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Article (hidden for ROLE_USER) */}
          {!isUser && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Artikal</label>
              <select
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                value={selectedArticleId}
                onChange={(e) => setSelectedArticleId(e.target.value)}
              >
                <option value="">Svi artikli</option>
                {articles.map((a) => (
                  <option key={a.article_id} value={a.article_id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            onClick={handleFetch}
            disabled={!selectedYear || loading}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Ucitavanje..." : "Prikazi"}
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center items-center min-h-[200px]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Content */}
      {!loading && trendData && (
        <>
          {/* Summary Cards */}
          <div className={`grid gap-4 mb-6 ${isUser ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1 sm:grid-cols-2 md:grid-cols-4"}`}>
            <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{quantityLabel}</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.totalQuantity)} <span className="text-sm font-medium text-gray-500">KG</span></p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{revenueLabel}</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.totalRevenue)} <span className="text-sm font-medium text-gray-500">RSD</span></p>
            </div>
            {!isUser && (
              <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Prosecna porudzbina</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.avgOrder)} <span className="text-sm font-medium text-gray-500">RSD</span></p>
              </div>
            )}
            <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Aktivni meseci</p>
              <p className="text-2xl font-bold text-gray-900">{summary.activeMonths} <span className="text-sm font-medium text-gray-500">/ 12</span></p>
            </div>
          </div>

          {/* Bar Chart */}
          {chartData.length > 0 && (
            <div className="bg-white rounded-xl p-6 mb-6 border border-gray-200 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Mesecni pregled</h2>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" tick={{ fill: "#6b7280", fontSize: 12 }} axisLine={{ stroke: "#d1d5db" }} tickLine={{ stroke: "#d1d5db" }} />
                  <YAxis
                    yAxisId="left"
                    orientation="left"
                    tick={{ fill: "#2563eb", fontSize: 12 }}
                    axisLine={{ stroke: "#d1d5db" }}
                    tickLine={{ stroke: "#d1d5db" }}
                    label={{ value: "Kvantitet (KG)", angle: -90, position: "insideLeft", fill: "#2563eb", fontSize: 12 }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fill: "#059669", fontSize: 12 }}
                    axisLine={{ stroke: "#d1d5db" }}
                    tickLine={{ stroke: "#d1d5db" }}
                    label={{ value: "Prihod (RSD)", angle: 90, position: "insideRight", fill: "#059669", fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#fff", border: "1px solid #e5e7eb", borderRadius: "8px", color: "#111827", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
                    labelStyle={{ color: "#6b7280", fontWeight: 600 }}
                  />
                  <Legend wrapperStyle={{ color: "#374151" }} />
                  <Bar yAxisId="left" dataKey="Kvantitet" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="right" dataKey="Prihod" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Data Table */}
          {chartData.length > 0 && (
            <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="px-5 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wide">Mesec</th>
                      <th className="px-5 py-3 text-right text-xs font-bold text-gray-900 uppercase tracking-wide">Kvantitet (KG)</th>
                      <th className="px-5 py-3 text-right text-xs font-bold text-gray-900 uppercase tracking-wide">Prihod (RSD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chartData.map((row, idx) => (
                      <tr key={idx} className={`border-b border-gray-100 hover:bg-blue-50/50 transition-colors ${idx % 2 !== 0 ? "bg-gray-50/60" : ""}`}>
                        <td className="px-5 py-3 text-sm text-gray-700 font-medium">{row.name}</td>
                        <td className="px-5 py-3 text-sm text-gray-700 text-right">{formatCurrency(row.Kvantitet)}</td>
                        <td className="px-5 py-3 text-sm text-gray-700 text-right">{formatCurrency(row.Prihod)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-200 bg-gray-50">
                      <td className="px-5 py-3 text-sm font-bold text-gray-900">Ukupno</td>
                      <td className="px-5 py-3 text-sm font-bold text-gray-900 text-right">
                        {formatCurrency(summary.totalQuantity)}
                      </td>
                      <td className="px-5 py-3 text-sm font-bold text-gray-900 text-right">
                        {formatCurrency(summary.totalRevenue)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Empty state */}
      {!loading && !trendData && (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-200 shadow-sm">
          <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className="text-gray-500">Izaberite godinu i kliknite "Prikazi" za prikaz statistike.</p>
        </div>
      )}
    </div>
  );
};

export default AnalyticsPage;
